import type { ScheduleRow } from './schema';
import type { ScheduleEntry, EntryStatus } from './types';
import { nextMarketDay } from '../../utils/marketStatus';

// ---------------------------------------------------------------------------
// Row → entry conversion
// ---------------------------------------------------------------------------

export function rowToEntry(row: ScheduleRow): ScheduleEntry {
  return {
    date: row.date,
    startTime: row.start_time,
    endTime: row.end_time,
    stage: row.stage,
    musician: row.scheduled_musician,
  };
}

// ---------------------------------------------------------------------------
// Date parsing
// ---------------------------------------------------------------------------

/**
 * Parses a date string from the Schedule sheet into a Date. Sheets returns
 * FORMATTED_VALUE which can be "M/D/YYYY", "YYYY-MM-DD", or locale-formatted
 * strings like "May 31, 2026". Returns null if unparseable.
 */
export function parseScheduleDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    // new Date() can produce a midnight UTC date for "YYYY-MM-DD" strings,
    // which shifts the local date by one day behind UTC. Normalise to
    // local midnight so the date comparison in filterScheduleForStage works.
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
      const parts = dateStr.trim().split('-').map(Number);
      const y = parts[0] ?? 0;
      const m = parts[1] ?? 1;
      const day = parts[2] ?? 1;
      return new Date(y, m - 1, day);
    }
    return d;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Time parsing
// ---------------------------------------------------------------------------

/**
 * Parses a time string like "9:00 AM", "09:00", or "9:00:00" into a Date on
 * `referenceDate`. Returns null if the string cannot be parsed.
 */
export function parseScheduleTime(timeStr: string, referenceDate: Date): Date | null {
  if (!timeStr) return null;
  const s = timeStr.trim();

  let hours = 0;
  let minutes = 0;

  // "H:MM AM/PM" or "HH:MM AM/PM"
  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    hours = parseInt(ampm[1] as string, 10);
    minutes = parseInt(ampm[2] as string, 10);
    const period = (ampm[3] as string).toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
  } else {
    // "HH:MM" or "HH:MM:SS" (24-hour)
    const military = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!military) return null;
    hours = parseInt(military[1] as string, 10);
    minutes = parseInt(military[2] as string, 10);
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  const result = new Date(referenceDate);
  result.setHours(hours, minutes, 0, 0);
  return result;
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/**
 * Returns entries for the stage at `stageLocation` on the given market day,
 * sorted by start time. The schedule sheet's `Stage` column holds the
 * stage position's resolved location name (one stage per street is a
 * market-layout invariant). Match is case-insensitive and ignores
 * surrounding whitespace.
 */
export function filterScheduleForStage(
  entries: ScheduleEntry[],
  stageLocation: string,
  marketDay: Date,
): ScheduleEntry[] {
  const dayStr = marketDay.toDateString();
  return entries
    .filter((e) => {
      if (e.stage.trim().toLowerCase() !== stageLocation.trim().toLowerCase()) return false;
      const d = parseScheduleDate(e.date);
      return d !== null && d.toDateString() === dayStr;
    })
    .sort((a, b) => {
      const ta = parseScheduleTime(a.startTime, marketDay)?.getTime() ?? 0;
      const tb = parseScheduleTime(b.startTime, marketDay)?.getTime() ?? 0;
      return ta - tb;
    });
}

// ---------------------------------------------------------------------------
// Status classification
// ---------------------------------------------------------------------------

/**
 * Classifies each entry relative to `now`:
 *   past     — end time has passed (entries without an end time stay 'now')
 *   now      — currently performing
 *   next     — immediately follows the "now" entry
 *   upcoming — later in the day
 *
 * The "next" designation is assigned to at most one entry: the soonest
 * future entry after the current performer (or the first future entry if
 * nothing is currently on stage).
 */
export function classifyEntries(
  entries: ScheduleEntry[],
  now: Date,
  marketDay: Date,
): EntryStatus[] {
  const times = entries.map((e) => ({
    start: parseScheduleTime(e.startTime, marketDay),
    end: e.endTime ? parseScheduleTime(e.endTime, marketDay) : null,
  }));

  const statuses: EntryStatus[] = entries.map((_, i) => {
    const { start, end } = times[i] ?? { start: null, end: null };
    if (!start) return 'upcoming';
    // Only mark past when we have an explicit end time that has passed.
    // Without an end time, keep the entry as 'now' for the rest of the day
    // once it starts — there is no reliable boundary to flip it to past.
    if (end !== null && end < now) return 'past';
    if (start <= now) return 'now';
    return 'upcoming';
  });

  // Mark at most one entry as "next".
  const nowIdx = statuses.indexOf('now');
  const firstUpcomingIdx = statuses.indexOf('upcoming');
  const nextIdx = nowIdx !== -1 ? statuses.indexOf('upcoming', nowIdx + 1) : firstUpcomingIdx;
  if (nextIdx !== -1) statuses[nextIdx] = 'next';

  return statuses;
}

// ---------------------------------------------------------------------------
// Demo data (used in DEV when schedule API is unconfigured)
// ---------------------------------------------------------------------------

/** Builds demo schedule entries anchored to the next market day so the
 *  "Now Playing" / "Up Next" labels render correctly during local dev. */
export function getDemoScheduleEntries(now: Date = new Date()): ScheduleEntry[] {
  const saturday = nextMarketDay(now);
  const dateStr = `${saturday.getMonth() + 1}/${saturday.getDate()}/${saturday.getFullYear()}`;
  return [
    { date: dateStr, startTime: '10:00 AM', endTime: '11:45 AM', stage: 'Monument Square', musician: 'The Harvest Band' },
    { date: dateStr, startTime: '12:15 PM', endTime: '2:00 PM', stage: 'Monument Square', musician: 'Folk Trio' },
    { date: dateStr, startTime: '10:00 AM', endTime: '11:45 AM', stage: 'Lower River St.', musician: 'Morning Acoustics' },
    { date: dateStr, startTime: '12:15 PM', endTime: '2:00 PM', stage: 'Lower River St.', musician: 'The Roots Duo' },
  ];
}
